import {
  Button,
  Flex,
  Heading,
  List,
  ListItem,
  Text,
} from '@everlywell/ui-kit';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Link as GatsbyLink } from 'gatsby';
import pluralize from 'pluralize';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import analytics from 'utils/analytics';
import { getQuestLocations, QuestLocation } from 'utils/api/questApis';
import { ANALYTICS } from 'utils/constants/analytics';
import {
  isValidZipCode,
  isZipCodeInIneligibleQuestState,
} from 'utils/formHelpers';
import { generateKey } from 'utils/helpers';

import { ANIMATION_DURATION } from '../../QuestLocationFinder.constants';
import QuestLocationListItem from '../QuestLocationListItem';
import ViewNearbyButton from '../ViewNearbyButton';
import ZipCodeInputField from '../ZipCodeInputField';

export type QuestLocationFormAndListProps = {
  widgetState: 'landing' | 'list' | 'listItem';
  onSearchSubmit: () => void;
  onLocationClick: (location: QuestLocation) => void;
};

/**
 * [Add the description for what this component does here].
 */
const QuestLocationFormAndList = (props: QuestLocationFormAndListProps) => {
  const { widgetState, onSearchSubmit, onLocationClick } = props;
  const [searchedZipCode, setSearchedZipCode] = useState('');

  const useFormMethods = useForm<{
    zipCode: string;
  }>({
    defaultValues: {
      zipCode: '',
    },
  });

  const handleZipCodeSubmit = (data: { zipCode: string }) => {
    if (data.zipCode) {
      analytics.track({
        event: ANALYTICS.EVENTS.CLICKED_BUTTON,
        data: {
          label: 'searched for PSC locations',
        },
      });
      setSearchedZipCode(data.zipCode);
      if (ineligibleZipCode) {
        analytics.track({
          event: 'Restricted State Error',
          data: {
            label: `Ineligible Zip Code Submitted - ${searchedZipCode}`,
          },
        });
      }
      if (!isValidZipCode(searchedZipCode)) {
        analytics.track({
          event: 'Form validation error',
          data: {
            label: `Invalid ZIP Code - ${searchedZipCode}`,
          },
        });
      }

      onSearchSubmit();
    }
  };

  const zipCodeInput = useFormMethods.watch('zipCode', '');

  const {
    data: locationsData,
    isInitialLoading,
    isError: fetchingError,
  } = useQuery(
    ['questLocations', searchedZipCode],
    () => getQuestLocations(searchedZipCode),
    {
      enabled: !!searchedZipCode,
      retry: false,
    },
  );

  const isError =
    fetchingError || isZipCodeInIneligibleQuestState(parseInt(searchedZipCode));

  const ineligibleZipCode = isZipCodeInIneligibleQuestState(
    parseInt(searchedZipCode),
  );

  let patientServiceCenters: QuestLocation[] | undefined = undefined;
  if (
    zipCodeInput === searchedZipCode &&
    locationsData?.patient_service_centers &&
    !ineligibleZipCode
  ) {
    patientServiceCenters = locationsData.patient_service_centers;
  }

  const noLocationsAvailable = isError || patientServiceCenters?.length === 0;

  const handleLocationClick = (location: QuestLocation) => {
    onLocationClick(location);
  };

  const handleSexualHealthCTAClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: 'Explore Sexual Health',
      },
    });
  };

  return (
    <FormProvider {...useFormMethods}>
      <motion.div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          zIndex: 1,
        }}
        initial={{ opacity: 1 }}
        animate={
          {
            landing: { opacity: 1 },
            list: {
              opacity: 1,
            },
            listItem: {
              opacity: 0,
              display: 'none',
              transform: 'translateY(-10%)',
            },
          }[widgetState]
        }
        transition={{
          type: 'tween',
          duration: ANIMATION_DURATION,
        }}
      >
        {widgetState === 'landing' && (
          <Flex flexDirection={'column'}>
            <Heading size={'xl'}>
              In-person tests at any Quest
              <Text as="span" fontSize={'4xl'}>
                ®
              </Text>{' '}
              location
            </Heading>
            <Heading size={'xl'} color={'tints.darkGrey'}>
              Find one near you
            </Heading>
          </Flex>
        )}

        <motion.div
          layout={'position'}
          style={{ width: '100%' }}
          transition={{
            type: 'tween',
            duration: ANIMATION_DURATION,
          }}
        >
          <motion.form
            onSubmit={useFormMethods.handleSubmit(handleZipCodeSubmit)}
          >
            <Flex gap={[3, 6]} alignItems="flex-start">
              <ZipCodeInputField />
              <ViewNearbyButton
                onGetZipCode={(zipcode: string) => {
                  useFormMethods.setValue('zipCode', zipcode);
                  useFormMethods.handleSubmit(handleZipCodeSubmit)();
                }}
              />
            </Flex>
          </motion.form>

          <Flex
            marginTop={20}
            justifyContent="center"
            width="100%"
            display={
              noLocationsAvailable || isInitialLoading || ineligibleZipCode
                ? 'flex'
                : 'none'
            }
          >
            {isInitialLoading && !ineligibleZipCode && (
              <Text size="lg">Searching for locations...</Text>
            )}
            {noLocationsAvailable && !ineligibleZipCode && (
              <Text size="lg" color="tints.black" textAlign="center">
                Sorry, no service centers found, try a different zip code.
              </Text>
            )}
            {ineligibleZipCode && (
              <Flex gap={6} flexDirection={'column'} alignItems={'center'}>
                <Flex flexDirection={'column'}>
                  <Text size="lg" color="tints.black" textAlign="center">
                    Purchases not permitted in: AK, AZ, or HI.
                  </Text>
                  <Text size="lg" color="tints.uiGrey" textAlign="center">
                    You can also explore our at-home lab kits.
                  </Text>
                </Flex>

                <Button
                  as={GatsbyLink}
                  to="/products/?category=8"
                  variant={'secondary'}
                  colorScheme="greenCS"
                  onClick={handleSexualHealthCTAClick}
                >
                  Explore Sexual Health
                </Button>
              </Flex>
            )}
          </Flex>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, display: 'none' }}
          animate={
            {
              landing: { opacity: 0, display: 'none' },
              list: { opacity: 1, display: 'block' },
              listItem: { opacity: 1, display: 'block' },
            }[widgetState]
          }
          transition={{
            type: 'tween',
            duration: 0.5,
            delay: ANIMATION_DURATION,
          }}
        >
          <Flex flexDirection={'column'} gap={[3, null, 4]}>
            {patientServiceCenters && (
              <Text size="lg" marginTop={[3, 4]}>
                {patientServiceCenters.length > 3
                  ? '3+'
                  : patientServiceCenters.length}{' '}
                {pluralize('Clinic', patientServiceCenters.length)} found
              </Text>
            )}
            <List spacing={[2, null, 3]}>
              {[...(patientServiceCenters ?? [])]
                .slice(0, 3)
                ?.map((location) => (
                  <ListItem width={'100%'} key={generateKey()}>
                    <QuestLocationListItem
                      location={location}
                      handleOnClick={() => handleLocationClick(location)}
                    />
                  </ListItem>
                ))}
            </List>
          </Flex>
        </motion.div>
      </motion.div>
    </FormProvider>
  );
};

export default QuestLocationFormAndList;
